import React, { useState, useEffect, useRef, useCallback } from 'react';
import GenericModal from '../../components/Modal/GenericModal';
import authService, { SESSION_EXPIRED_EVENT } from '../../services/auth-service';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { updateAccessSelector } from '../../redux/common/roleAccess/selectors';
import { getApi, postApiWithParams } from '../../services/apiBase';
import { setting_pwd, logout_url } from '../../services/apiURL';

const EVENTS = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];
const MILLISECOND = 60000;
const WARNING_MINUTES = 2;

const IdleTimeOutHandler = (props) => {
    let history = useHistory();

    const { pwdSetting } = useSelector(updateAccessSelector);
    const [idleModal, setIdleModal] = useState(false);

    const [signoutTime, setSignoutTime] = useState(0);
    const [warningTime, setWarningTime] = useState(0);
    const userNameRef = useRef(localStorage.getItem("user"));

    // Store timeout IDs in refs so they persist across renders and can always be cleared
    const warnTimeoutRef = useRef(null);
    const logoutTimeoutRef = useRef(null);
    const countIntervalRef = useRef(null);
    const [count, setCount] = useState(0);

    const getLogoutTime = () => {
        getApi(setting_pwd).then((resp) => {
            if ((Object.keys(resp.data.data).length > 0)) {
                const { userTimeOut } = resp.data.data;
                const effectiveTimeout = userTimeOut <= 2 ? userTimeOut + 2 : userTimeOut;
                setWarningTime((effectiveTimeout * MILLISECOND) - (WARNING_MINUTES * MILLISECOND));
                setSignoutTime(effectiveTimeout * MILLISECOND);
            }
        }).catch((err) => {
            console.log(err)
        })
    }

    const goToSignout = useCallback(() => {
        authService.logout(() => history.push('/signout'));
    }, [history]);

    const logout = useCallback(() => {
        setIdleModal(false);
        setCount(0);
        // Session may already be gone (token cleared/expired / API will 401). Always reach
        // the signout screen even if the server logout call fails.
        if (!authService.isSessionActive()) {
            goToSignout();
            return;
        }
        const payload = { "username": userNameRef.current };
        postApiWithParams(`${logout_url}`, payload).then(goToSignout).catch(goToSignout);
    }, [history, goToSignout]);

    const warn = useCallback(() => {
        // Do not offer extend if auth is already dead (e.g. 401 login-required just fired).
        if (!authService.isSessionActive()) {
            goToSignout();
            return;
        }
        setIdleModal(true)
    }, [goToSignout]);

    // If API layer already decided the session is dead, close the idle prompt.
    useEffect(() => {
        const onSessionExpired = () => {
            setIdleModal(false);
            setCount(0);
        };
        window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
        return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    }, []);

    // Countdown timer when idle modal is open
    useEffect(() => {
        if (idleModal) {
            if (count >= (WARNING_MINUTES * 60)) {
                logout();
                return;
            }
            countIntervalRef.current = setInterval(() => {
                setCount(prev => prev + 1);
            }, 1000);
            return () => clearInterval(countIntervalRef.current);
        }
    }, [idleModal, count >= (WARNING_MINUTES * 60), logout]);

    // Fetch logout time on mount and when password settings change
    useEffect(() => {
        getLogoutTime();
    }, []);

    useEffect(() => {
        getLogoutTime();
    }, [pwdSetting]);

    // Main idle detection — event listeners + timeouts
    useEffect(() => {
        if (!signoutTime || !warningTime) return;

        const clearAllTimeouts = () => {
            if (warnTimeoutRef.current) {
                clearTimeout(warnTimeoutRef.current);
                warnTimeoutRef.current = null;
            }
            if (logoutTimeoutRef.current) {
                clearTimeout(logoutTimeoutRef.current);
                logoutTimeoutRef.current = null;
            }
        };

        const setAllTimeouts = () => {
            warnTimeoutRef.current = setTimeout(warn, warningTime);
            logoutTimeoutRef.current = setTimeout(logout, signoutTime);
        };

        const resetTimeout = () => {
            clearAllTimeouts();
            setAllTimeouts();
        };

        for (const event of EVENTS) {
            window.addEventListener(event, resetTimeout);
        }
        setAllTimeouts();

        return () => {
            for (const event of EVENTS) {
                window.removeEventListener(event, resetTimeout);
            }
            clearAllTimeouts();
        };
    }, [signoutTime, warningTime, warn, logout]);

    const extendSession = () => {
        // Idle countdown can outlast auth (JWT expired while the warning was up).
        if (!authService.isSessionActive()) {
            logout();
            return;
        }
        setIdleModal(false);
        setCount(0);
    }

    return (
        <GenericModal
            open={idleModal} onClose={setIdleModal}
            title={`Your session will expire in ${WARNING_MINUTES} minutes. Do you want to extend the session?`}
            buttonText={"Yes"}
            button2Text={"No"}
            onButtonPress={extendSession}
            onButton2Press={logout}
        />
    )
}

export default IdleTimeOutHandler;
